// [marker: renewals-member-select v2]
// [marker: renewals-tracker v1] overview: who still needs to renew, what's lapsing, latest batch results
// src/pages/RenewalsPage.js — list of renewal batches; create selects members + letterhead
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid';

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

export default function RenewalsPage() {
  const { isManagerOrAbove } = useAuth();
  const [batches, setBatches] = useState(null);
  const [creating, setCreating] = useState(false);
  const [overview, setOverview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/renewals/overview').then(res => setOverview(res.data)).catch(() => {});
  }, []);

  const load = () => {
    api.get('/renewals/batches')
      .then(res => setBatches(res.data || []))
      .catch(() => toast.error('Could not load batches.'));
  };
  useEffect(load, []);

  const handleCreate = async (formData) => {
    try {
      const res = await api.post('/renewals/batches', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Batch created with ${res.data.item_count} recipients.`);
      if (res.data.warnings?.length) {
        toast(res.data.warnings.slice(0, 3).join('\n'), { duration: 6000, icon: '⚠️' });
      }
      setCreating(false);
      navigate(`/renewals/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create batch.');
    }
  };

  if (batches === null) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Renewals</h1>
          <p className="page-subtitle">Who needs to renew, renewal batches, and reminders.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn btn-secondary" to="/renewals/templates">Templates (letter, email, envelope)</Link>
          {isManagerOrAbove() && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New Batch</button>
          )}
        </div>
      </div>

      {overview && (
        <RenewalOverview
          data={overview}
          canCreate={isManagerOrAbove()}
          onStartDue={() => setCreating('due')}
          onOpenBatch={bid => navigate(`/renewals/${bid}`)}
        />
      )}

      <h2 style={{ fontSize: '1.05rem', margin: '20px 0 10px' }}>Renewal batches</h2>
      {batches.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: 'var(--gray-500)' }}>
            <p style={{ marginBottom: 16 }}>No renewal batches yet.</p>
            {isManagerOrAbove() && (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Create First Batch</button>
            )}
          </div>
        </div>
      ) : (
        /* [marker: step1-templates-grids v1] Excel-style batches list */
        <DataGrid
          gridKey="renewal-batches"
          rows={batches}
          onRowClick={b => navigate(`/renewals/${b.id}`)}
          height="calc(100vh - 260px)"
          totals={['item_count', 'sent_count']}
          columns={[
            { key: 'name', label: 'Name', width: 260, render: b => <strong>{b.name}</strong> },
            { key: 'period', label: 'Period', width: 140, get: b => [b.renewal_month, b.renewal_year].filter(Boolean).join(' ') },
            { key: 'status', label: 'Status', width: 120, render: b => <StatusBadge status={b.status} /> },
            { key: 'item_count', label: 'Recipients', width: 100, type: 'number' },
            { key: 'ready_count', label: 'Ready', width: 80, type: 'number' },
            { key: 'sent_count', label: 'Sent', width: 80, type: 'number' },
            { key: 'paid_count', label: 'Paid', width: 80, type: 'number' },
            { key: 'created_at', label: 'Created', width: 110, type: 'date' },
          ]}
        />
      )}

      {creating && <CreateBatchModal preset={creating === 'due' ? 'due' : ''} onClose={() => setCreating(false)} onCreate={handleCreate} />}
    </div>
  );
}

function CreateBatchModal({ onClose, onCreate, preset }) {
  const thisYear = new Date().getFullYear();
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(thisYear);
  const [name, setName] = useState('');
  const [letterheadFile, setLetterheadFile] = useState(null);
  const [savedLetterhead, setSavedLetterhead] = useState(null); // [marker: finish-up v1] Settings → Letterhead
  const [saving, setSaving] = useState(false);
  const letterRef = useRef(null);

  // Member selection (replaces the Excel recipient upload)
  const [memberTypes, setMemberTypes] = useState([]);
  const [filters, setFilters] = useState({ status: preset === 'due' ? 'due' : '', membership_type_id: '', renewal_month: '' });
  const autoSelect = useRef(preset === 'due'); // [marker: renewals-tracker v1] pre-tick everyone who's due
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (!name || name === `${month} ${year} Renewals`) {
      setName(`${month} ${year} Renewals`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => {
    api.get('/letterhead').then(res => setSavedLetterhead(res.data?.docx || null)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/lookups/membership-types')
      .then(res => setMemberTypes(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingMembers(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (!v) return;
      if (k === 'status' && v === 'due') params.append('due', '1');
      else params.append(k, v);
    });
    api.get(`/renewals/eligible-members?${params}`)
      .then(res => {
        const list = res.data?.data || [];
        setMembers(list);
        if (autoSelect.current) {
          autoSelect.current = false;
          setSelectedIds(new Set(list.filter(m => m.email).map(m => m.id)));
        }
      })
      .catch(() => toast.error('Could not load members.'))
      .finally(() => setLoadingMembers(false));
  }, [filters]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const selectable = members.filter(m => m.email);
  const allSelected = selectable.length > 0 && selectable.every(m => selectedIds.has(m.id));

  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allSelected) selectable.forEach(m => next.delete(m.id));
    else selectable.forEach(m => next.add(m.id));
    return next;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.size === 0) return toast.error('Select at least one member.');
    if (!letterheadFile && !savedLetterhead) return toast.error('Choose a letterhead .docx file (or save one under Settings → Letterhead).');
    if (letterheadFile && !letterheadFile.name.toLowerCase().endsWith('.docx')) {
      return toast.error('Letterhead must be a .docx file.');
    }

    const fd = new FormData();
    fd.append('name', name.trim());
    fd.append('renewalMonth', month);
    fd.append('renewalYear', String(year));
    fd.append('businessIds', JSON.stringify(Array.from(selectedIds)));
    if (letterheadFile) fd.append('letterhead', letterheadFile);

    setSaving(true);
    await onCreate(fd);
    setSaving(false);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0 }}>New Renewal Batch</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 20 }}>
          <div style={fg}>
            <label style={lbl}>Batch Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} style={input} required />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...fg, flex: 1 }}>
              <label style={lbl}>Renewal Month</label>
              <select value={month} onChange={e => setMonth(e.target.value)} style={input}>
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ ...fg, flex: 1 }}>
              <label style={lbl}>Renewal Year</label>
              <input type="number" min="2020" max="2099" value={year} onChange={e => setYear(e.target.value)} style={input} />
            </div>
          </div>

          <div style={fg}>
            <label style={lbl}>Select Members *</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <select style={{ ...input, flex: '1 1 30%', width: 'auto' }} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                <option value="">All Statuses</option>
                <option value="due">Due now — not yet renewed</option>
                <option value="active">Active</option>
                <option value="grace_period">Grace Period</option>
                <option value="lapsed">Lapsed</option>
                <option value="non_member">Non-Member</option>
              </select>
              <select style={{ ...input, flex: '1 1 30%', width: 'auto' }} value={filters.membership_type_id} onChange={e => setFilter('membership_type_id', e.target.value)}>
                <option value="">All Types</option>
                {memberTypes.map(mt => <option key={mt.id} value={mt.id}>{mt.name}</option>)}
              </select>
              <select style={{ ...input, flex: '1 1 30%', width: 'auto' }} value={filters.renewal_month} onChange={e => setFilter('renewal_month', e.target.value)}>
                <option value="">All Renewal Months</option>
                <option value="January">January</option>
                <option value="July">July</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0} />
                Select all{selectable.length ? ` (${selectable.length})` : ''}
              </label>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-600)', fontWeight: 600 }}>
                {selectedIds.size} selected
              </span>
            </div>

            <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, maxHeight: 260, overflowY: 'auto', background: '#fff' }}>
              {loadingMembers ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.85rem' }}>Loading members…</div>
              ) : members.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                  No members match these filters.
                </div>
              ) : members.map(m => {
                const noEmail = !m.email;
                const checked = selectedIds.has(m.id);
                return (
                  <label
                    key={m.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderBottom: '1px solid var(--gray-100)',
                      cursor: noEmail ? 'not-allowed' : 'pointer',
                      opacity: noEmail ? 0.55 : 1,
                    }}
                  >
                    <input type="checkbox" checked={checked} disabled={noEmail} onChange={() => toggleOne(m.id)} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.business_name}</span>
                      <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                        {noEmail
                          ? '⚠ No contact email — will be skipped'
                          : (m.contact_name ? `${m.contact_name} · ${m.email}` : m.email)}
                      </span>
                    </span>
                    {m.membership_type_name && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                        {m.membership_type_name}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div style={hint}>
              Recipients come from your member database. Each renewal is emailed to the primary contact's email, and their name fills the letter's name fields. Members whose primary contact has no email are skipped.
            </div>
          </div>

          <div style={fg}>
            <label style={lbl}>Letterhead (Word .docx){savedLetterhead ? '' : ' *'}</label>
            {savedLetterhead && !letterheadFile && (
              <div style={{ ...hint, marginBottom: 6, color: 'var(--success)' }}>
                ✓ Using the saved letterhead “{savedLetterhead.name}” (Settings → Letterhead). Choose a file only to use a different one for this batch.
              </div>
            )}
            <input
              ref={letterRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={e => setLetterheadFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <button type="button" onClick={() => letterRef.current?.click()} style={fileBtn}>
              {letterheadFile ? `📄 ${letterheadFile.name}` : savedLetterhead ? '📄 Use a different letterhead…' : '📄 Choose letterhead .docx…'}
            </button>
            <div style={hint}>
              Your NJJBA letterhead with placeholders like <code>{`{{First Name}}`}</code>, <code>{`{{Business Name}}`}</code>, <code>{`{{Address}}`}</code>.
              Use the standard tokens listed on the Email Template page.
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create Batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// [marker: renewals-tracker v1]
function RenewalOverview({ data, canCreate, onStartDue, onOpenBatch }) {
  const [showAll, setShowAll] = useState(false);
  const { counts, due, latestBatch, graceDays } = data;
  const months = Math.round((graceDays || 90) / 30);
  const fmt = d => {
    const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  };
  const list = showAll ? due : due.slice(0, 12);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <OvTile label="Not yet renewed" sub="due this month or earlier" value={counts.due} tone={counts.due ? 'warn' : 'ok'} />
        <OvTile label="Lapsing in 30 days" sub={`members lapse after ${months} months`} value={counts.lapsing_30} tone={counts.lapsing_30 ? 'bad' : 'ok'} />
        <OvTile label="In grace period" value={counts.in_grace} />
        <OvTile label="Renewed this month" value={counts.renewed_this_month} tone="ok" />
        <OvTile label="Lapsed" value={counts.lapsed} />
      </div>

      {latestBatch && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: 14 }}>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 700 }}>Latest batch</div>
              <strong>{latestBatch.name}</strong>
            </div>
            <span>{latestBatch.sent} of {latestBatch.total} sent</span>
            <span style={{ color: '#15803d', fontWeight: 600 }}>{latestBatch.paid} paid</span>
            <span style={{ color: '#92400e', fontWeight: 600 }}>{latestBatch.no_response} no response yet</span>
            <button className="btn btn-secondary btn-sm" onClick={() => onOpenBatch(latestBatch.id)}>Open, remind the unpaid</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="card-title">Not yet renewed ({due.length})</span>
          {canCreate && due.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={onStartDue}>Send renewals to these {due.length}</button>
          )}
        </div>
        {due.length === 0 ? (
          <div className="card-body" style={{ color: 'var(--gray-500)' }}>Everyone due so far has renewed. 🎉</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={th}>Member</th><th style={th}>Type</th><th style={th}>Renewal date</th>
                  <th style={th}>Lapses</th><th style={th}>Last renewal email</th>
                </tr>
              </thead>
              <tbody>
                {list.map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={td}><Link to={`/members/${m.id}`} style={{ fontWeight: 600 }}>{m.business_name}</Link></td>
                    <td style={td}>{m.membership_type_name || '—'}</td>
                    <td style={td}>{fmt(m.next_renewal_date)}</td>
                    <td style={{ ...td, color: m.days_to_lapse != null && m.days_to_lapse <= 30 ? '#991b1b' : undefined, fontWeight: m.days_to_lapse != null && m.days_to_lapse <= 30 ? 600 : 400 }}>
                      {m.days_to_lapse == null ? '—' : m.days_to_lapse <= 0 ? 'today' : `in ${m.days_to_lapse} days`}
                    </td>
                    <td style={td}>{m.last_followup_at || m.last_sent_at ? fmt(String(m.last_followup_at && m.last_followup_at > (m.last_sent_at || '') ? m.last_followup_at : m.last_sent_at)) : <span style={{ color: '#92400e' }}>Not sent yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {due.length > 12 && (
              <div style={{ padding: 10, textAlign: 'center' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAll(v => !v)}>{showAll ? 'Show fewer' : `Show all ${due.length}`}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OvTile({ label, sub, value, tone }) {
  const color = tone === 'bad' ? '#991b1b' : tone === 'warn' ? '#92400e' : tone === 'ok' ? '#15803d' : 'var(--gray-800)';
  return (
    <div className="card" style={{ padding: 12, margin: 0 }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft:   { bg: 'var(--gray-100)', fg: 'var(--gray-700)', label: 'Draft' },
    sending: { bg: '#fef3c7', fg: '#92400e', label: 'Sending…' },
    sent:    { bg: '#dcfce7', fg: '#15803d', label: 'Sent' },
    partial: { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
    failed:  { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      fontSize: '0.75rem', fontWeight: 700, padding: '2px 10px',
      borderRadius: 10, background: s.bg, color: s.fg, textTransform: 'uppercase',
    }}>{s.label}</span>
  );
}

const th = { textAlign: 'left', padding: '12px 14px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '12px 14px', fontSize: '0.9rem', color: 'var(--gray-800)' };
const fg = { marginBottom: 14 };
const lbl = { display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem' };
const input = { width: '100%', padding: '8px 12px', fontSize: '0.95rem', border: '1px solid var(--gray-300)', borderRadius: 6, background: '#fff', boxSizing: 'border-box' };
const hint = { fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.4 };
const fileBtn = { width: '100%', padding: '10px 12px', fontSize: '0.9rem', border: '2px dashed var(--gray-300)', borderRadius: 6, background: 'var(--gray-50)', cursor: 'pointer', textAlign: 'left', color: 'var(--gray-700)' };
const overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal = { background: '#fff', borderRadius: 8, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const modalHeader = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--gray-200)' };
const closeBtn = { background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: 'var(--gray-500)', padding: 0, lineHeight: 1 };
